import React, { useState } from "react";

import { Button } from "@/components/primitives/Button";
import { Input } from "@/components/primitives/Input";
import { Select } from "@/components/primitives/Select";
import type { DbConnection, DbConnectionCreate, DbType } from "@/types";

interface DbConnectionFormProps {
  connection?: DbConnection | null;
  onSubmit: (payload: DbConnectionCreate) => Promise<boolean>;
  onCancel: () => void;
  onDeploy?: () => Promise<boolean>;
  onResetToken?: () => Promise<boolean>;
  onUninstall?: () => Promise<boolean>;
  isDeploying?: boolean;
  isResettingToken?: boolean;
  isUninstalling?: boolean;
}

const DB_TYPE_OPTIONS: { value: DbType; label: string }[] = [
  { value: "postgresql", label: "PostgreSQL" },
  { value: "sqlite", label: "SQLite" },
  { value: "mongodb", label: "MongoDB" },
];

const SQLITE_MODE_OPTIONS = [
  { value: "direct", label: "Local file" },
  { value: "agent", label: "Remote agent" },
];

const connectionToFormState = (connection: DbConnection | null | undefined) => {
  const conn = connection?.connection ?? {};
  return {
    name: connection?.name ?? "",
    db_type: connection?.db_type ?? "postgresql",
    host: (conn.host as string) ?? "",
    port: conn.port != null ? String(conn.port) : "",
    username: (conn.username as string) ?? "",
    password: (conn.password as string) ?? "",
    database: (conn.database as string) ?? "",
    connection_string: (conn.connection_string as string) ?? "",
    ssl: Boolean(conn.ssl),
    sqlite_mode: (conn.mode as "direct" | "agent") || "direct",
    ssh_host: (conn.ssh_host as string) ?? "",
    ssh_port: conn.ssh_port != null ? String(conn.ssh_port) : "22",
    ssh_username: (conn.ssh_username as string) ?? "",
    ssh_password: (conn.ssh_password as string) ?? "",
    ssh_private_key: (conn.ssh_private_key as string) ?? "",
    sudo_password: (conn.sudo_password as string) ?? "",
    agent_url: (conn.agent_url as string) ?? "",
    service_port: conn.service_port != null ? String(conn.service_port) : "",
    agent_token: (conn.agent_token as string) ?? "",
  };
};

export const DbConnectionForm: React.FC<DbConnectionFormProps> = ({
  connection,
  onSubmit,
  onCancel,
  onDeploy,
  onResetToken,
  onUninstall,
  isDeploying,
  isResettingToken,
  isUninstalling,
}) => {
  const [values, setValues] = useState(() => connectionToFormState(connection));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = Boolean(connection);
  const isSqliteAgent = values.db_type === "sqlite" && values.sqlite_mode === "agent";

  const update = <K extends keyof typeof values>(key: K, value: (typeof values)[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = (): DbConnectionCreate => {
    const payload: Record<string, unknown> = {};

    if (values.db_type === "sqlite" && values.sqlite_mode === "direct") {
      if (values.database) payload.database = values.database;
    } else if (isSqliteAgent) {
      payload.mode = "agent";
      if (values.ssh_host) payload.ssh_host = values.ssh_host;
      if (values.ssh_port) payload.ssh_port = parseInt(values.ssh_port, 10);
      if (values.ssh_username) payload.ssh_username = values.ssh_username;
      if (values.ssh_password) payload.ssh_password = values.ssh_password;
      if (values.ssh_private_key) payload.ssh_private_key = values.ssh_private_key;
      if (values.sudo_password) payload.sudo_password = values.sudo_password;
      if (values.database) payload.db_path = values.database;
      if (values.agent_url) payload.agent_url = values.agent_url;
      if (values.service_port) payload.service_port = parseInt(values.service_port, 10);
      if (values.agent_token) payload.agent_token = values.agent_token;
    } else {
      if (values.host) payload.host = values.host;
      if (values.port) payload.port = parseInt(values.port, 10);
      if (values.username) payload.username = values.username;
      if (values.password) payload.password = values.password;
      if (values.database) payload.database = values.database;
      if (values.connection_string) payload.connection_string = values.connection_string;
      payload.ssl = values.ssl;
    }

    return {
      name: values.name.trim(),
      db_type: values.db_type,
      connection: payload,
      is_active: true,
    };
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!values.name.trim()) {
      setError("Connection name is required");
      return;
    }

    if (isSqliteAgent) {
      if (!values.ssh_host) {
        setError("SSH host is required for remote SQLite agent");
        return;
      }
      if (!values.ssh_username) {
        setError("SSH username is required for remote SQLite agent");
        return;
      }
      if (!values.ssh_password && !values.ssh_private_key) {
        setError("SSH password or private key is required for remote SQLite agent");
        return;
      }
      if (!values.sudo_password) {
        setError("Sudo password is required for remote SQLite agent");
        return;
      }
      if (!values.database) {
        setError("Remote database path is required for remote SQLite agent");
        return;
      }
    }

    const payload = buildPayload();
    setIsLoading(true);
    const success = await onSubmit(payload);
    setIsLoading(false);
    if (!success) {
      setError("Failed to save connection");
    }
  };

  const handleDeploy = async () => {
    if (!onDeploy) return;
    setError(null);
    const success = await onDeploy();
    if (!success) {
      setError("Agent deployment failed");
    }
  };

  const handleResetToken = async () => {
    if (!onResetToken) return;
    setError(null);
    const success = await onResetToken();
    if (!success) {
      setError("Failed to reset agent token");
    }
  };

  const handleUninstall = async () => {
    if (!onUninstall) return;
    setError(null);
    if (!window.confirm("Uninstall the remote agent? This stops the service and removes files from the remote server.")) {
      return;
    }
    const success = await onUninstall();
    if (!success) {
      setError("Failed to uninstall agent");
    }
  };

  const renderDirectFields = () => (
    <>
      <Input
        label="Host"
        value={values.host}
        onChange={(e) => update("host", e.target.value)}
        placeholder="localhost"
      />
      <Input
        label="Port"
        value={values.port}
        onChange={(e) => update("port", e.target.value)}
        placeholder={values.db_type === "mongodb" ? "27017" : "5432"}
      />
      <Input
        label="Username"
        value={values.username}
        onChange={(e) => update("username", e.target.value)}
      />
      <Input
        label="Password"
        type="password"
        value={values.password}
        onChange={(e) => update("password", e.target.value)}
      />
      <Input
        label="Database"
        value={values.database}
        onChange={(e) => update("database", e.target.value)}
        placeholder="servicehealthmonitor"
      />
      <Input
        label="Connection string (optional)"
        value={values.connection_string}
        onChange={(e) => update("connection_string", e.target.value)}
        placeholder={
          values.db_type === "mongodb"
            ? "mongodb://user:pass@host/db"
            : "postgresql://user:pass@host/db"
        }
      />
    </>
  );

  const renderSqliteFields = () => (
    <>
      <Select
        label="Connection mode"
        value={values.sqlite_mode}
        options={SQLITE_MODE_OPTIONS}
        onChange={(e) => update("sqlite_mode", e.target.value as "direct" | "agent")}
      />
      {values.sqlite_mode === "direct" ? (
        <Input
          label="Database file path"
          value={values.database}
          onChange={(e) => update("database", e.target.value)}
          placeholder="/path/to/database.db"
        />
      ) : (
        <div className="col-span-full flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="SSH host"
              value={values.ssh_host}
              onChange={(e) => update("ssh_host", e.target.value)}
              placeholder="203.0.113.10"
            />
            <Input
              label="SSH port"
              value={values.ssh_port}
              onChange={(e) => update("ssh_port", e.target.value)}
              placeholder="22"
            />
            <Input
              label="SSH username"
              value={values.ssh_username}
              onChange={(e) => update("ssh_username", e.target.value)}
              placeholder="root"
            />
            <Input
              label="SSH password"
              type="password"
              value={values.ssh_password}
              onChange={(e) => update("ssh_password", e.target.value)}
            />
            <Input
              label="Sudo password"
              type="password"
              value={values.sudo_password}
              onChange={(e) => update("sudo_password", e.target.value)}
            />
            <Input
              label="Remote database path"
              value={values.database}
              onChange={(e) => update("database", e.target.value)}
              placeholder="/opt/example/example.db"
            />
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Or use an SSH private key instead of a password:
          </div>
          <textarea
            value={values.ssh_private_key}
            onChange={(e) => update("ssh_private_key", e.target.value)}
            placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
            rows={4}
            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-softnu-500 focus:outline-none focus:ring-2 focus:ring-softnu-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />

          {isEditing && (
            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Remote agent
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleDeploy}
                  isLoading={isDeploying}
                >
                  {values.agent_url ? "Re-deploy agent" : "Deploy agent"}
                </Button>
              </div>
              {!values.agent_url && !values.agent_token && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  Deploy the agent to generate the agent URL and token before testing.
                </div>
              )}
              {values.agent_url && (
                <div className="space-y-1 text-xs text-slate-600 dark:text-slate-300">
                  <p>
                    <span className="font-medium">Agent URL:</span> {values.agent_url}
                  </p>
                  {values.service_port && (
                    <p>
                      <span className="font-medium">Port:</span> {values.service_port}
                    </p>
                  )}
                </div>
              )}
              {values.agent_url && (
                <div className="flex justify-end gap-2">
                  {onUninstall && (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={handleUninstall}
                      isLoading={isUninstalling}
                    >
                      Uninstall agent
                    </Button>
                  )}
                  {onResetToken && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleResetToken}
                      isLoading={isResettingToken}
                    >
                      Reset agent token
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error && (
        <div
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Name"
          value={values.name}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Production PostgreSQL"
          required
        />
        <Select
          label="Database type"
          value={values.db_type}
          options={DB_TYPE_OPTIONS}
          onChange={(e) => update("db_type", e.target.value as DbType)}
        />
        {values.db_type === "sqlite" ? renderSqliteFields() : renderDirectFields()}
      </div>

      {values.db_type !== "sqlite" && (
        <label className="flex cursor-pointer items-center gap-3 text-sm font-medium text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            checked={values.ssl}
            onChange={(e) => update("ssl", e.target.checked)}
            className="h-5 w-5 rounded border-slate-300 text-softnu-600 focus:ring-softnu-500 dark:border-slate-700 dark:bg-slate-800"
          />
          Use SSL
        </label>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          {connection ? "Save changes" : "Create connection"}
        </Button>
      </div>
    </form>
  );
};
